import { Fragment, useMemo, useState } from 'react'
import {
  addMonths,
  eachDayOfInterval,
  endOfMonth,
  endOfWeek,
  format,
  isBefore,
  isSameDay,
  isSameMonth,
  startOfMonth,
  startOfWeek,
  subMonths,
} from 'date-fns'
import clsx from 'clsx'

interface DatePickerProps {
  selectedDate: Date | null
  onChange: (date: Date) => void
  closedDates?: string[]
  minDate?: Date
  openingHours?: {
    [day: string]: {
      enabled: boolean
      intervals: Array<{ start: string; end: string }>
    }
  }
}

const DatePicker = ({
  selectedDate,
  onChange,
  closedDates = [],
  minDate = new Date(),
  openingHours,
}: DatePickerProps) => {
  const initialMonth = selectedDate ? startOfMonth(selectedDate) : startOfMonth(minDate)
  const [visibleMonth, setVisibleMonth] = useState<Date>(initialMonth)
  const closedDateSet = useMemo(() => new Set(closedDates), [closedDates])

  const allowedWeekdays = useMemo(() => {
    if (!openingHours) {
      return new Set([4, 5, 6])
    }

    const dayNameToNumber: Record<string, number> = {
      sunday: 0,
      monday: 1,
      tuesday: 2,
      wednesday: 3,
      thursday: 4,
      friday: 5,
      saturday: 6,
    }

    const enabled = new Set<number>()
    Object.entries(openingHours).forEach(([day, config]) => {
      if (config?.enabled) {
        const dayNum = dayNameToNumber[day.toLowerCase()]
        if (dayNum !== undefined) {
          enabled.add(dayNum)
        }
      }
    })

    console.log('📅 [DATEPICKER] Allowed weekdays:', Array.from(enabled))
    return enabled.size > 0 ? enabled : new Set([4, 5, 6])
  }, [openingHours])

  const weeks = useMemo(() => {
    const start = startOfWeek(startOfMonth(visibleMonth), { weekStartsOn: 1 })
    const end = endOfWeek(endOfMonth(visibleMonth), { weekStartsOn: 1 })

    const days = eachDayOfInterval({ start, end })
    const chunkSize = 7
    const slices = []
    for (let i = 0; i < days.length; i += chunkSize) {
      slices.push(days.slice(i, i + chunkSize))
    }
    return slices
  }, [visibleMonth])

  const isSelectable = (date: Date) => {
    const dayKey = format(date, 'yyyy-MM-dd')
    if (closedDateSet.has(dayKey)) return false
    if (isBefore(date, startOfDay(minDate))) return false
    if (!allowedWeekdays.has(date.getDay())) return false
    return true
  }

  const handleSelect = (day: Date) => {
    if (!isSelectable(day)) return
    console.log('[DATEPICKER] Date clicked:', day)
    console.log(
      '[DATEPICKER] Day name:',
      day?.toLocaleDateString('en-US', {
        weekday: 'long',
      }),
    )
    onChange(day)
  }

  const goToPreviousMonth = () => {
    const previous = subMonths(visibleMonth, 1)
    setVisibleMonth(previous)
  }

  const goToNextMonth = () => {
    const next = addMonths(visibleMonth, 1)
    setVisibleMonth(next)
  }

  return (
    <div className="superz-widget-panel p-6">
      <header className="mb-4 flex items-center justify-between">
        <div>
          <p className="text-xs uppercase tracking-[0.3em] text-widget-accent/70">Select Your Experience</p>
          <h2 className="font-display text-3xl text-widget-accent">{format(visibleMonth, 'MMMM yyyy')}</h2>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={goToPreviousMonth}
            className="rounded-full border border-widget-primary/30 bg-widget-primary/10 p-2 text-widget-accent transition hover:bg-widget-primary/15 focus:outline-none focus-visible:ring-2 focus-visible:ring-widget-secondary focus-visible:ring-offset-2 focus-visible:ring-offset-widget-primary"
            aria-label="Previous month"
          >
            ‹
          </button>
          <button
            type="button"
            onClick={goToNextMonth}
            className="rounded-full border border-widget-primary/30 bg-widget-primary/10 p-2 text-widget-accent transition hover:bg-widget-primary/15 focus:outline-none focus-visible:ring-2 focus-visible:ring-widget-secondary focus-visible:ring-offset-2 focus-visible:ring-offset-widget-primary"
            aria-label="Next month"
          >
            ›
          </button>
        </div>
      </header>

      <div className="grid grid-cols-7 gap-2 text-center text-xs font-semibold uppercase tracking-[0.25em] text-widget-accent/50">
        {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map((day) => (
          <span key={day}>{day}</span>
        ))}
      </div>

      <div className="mt-4 grid grid-cols-7 gap-2 text-sm">
        {weeks.map((week, weekIndex) => (
          <Fragment key={weekIndex}>
            {week.map((day) => {
              const selectable = isSelectable(day)
              const selected = selectedDate ? isSameDay(day, selectedDate) : false
              const isCurrentMonth = isSameMonth(day, visibleMonth)

              return (
                <button
                  key={day.toISOString()}
                  type="button"
                  onClick={() => handleSelect(day)}
                  disabled={!selectable}
                  aria-pressed={selected}
                  aria-label={format(day, 'EEEE, MMMM do, yyyy')}
                  className={clsx(
                    'aspect-square rounded-xl border text-sm transition-all duration-200 ease-in-out focus:outline-none focus-visible:ring-2 focus-visible:ring-widget-secondary focus-visible:ring-offset-2 focus-visible:ring-offset-widget-primary',
                    selectable
                      ? selected
                        ? 'border-widget-secondary bg-widget-secondary/20 text-widget-secondary shadow-lg hover:border-widget-secondary hover:bg-widget-secondary/20 hover:text-widget-secondary hover:scale-105'
                        : 'border-widget-primary/30 bg-widget-primary/10 text-widget-accent/80 hover:border-widget-secondary/50 hover:bg-widget-secondary/10 hover:text-widget-secondary hover:scale-105'
                      : 'border-transparent text-widget-accent/20 opacity-60',
                    !isCurrentMonth && 'text-widget-accent/15',
                  )}
                >
                  <span className="block font-semibold">{format(day, 'd')}</span>
                </button>
              )
            })}
          </Fragment>
        ))}
      </div>

      <div className="mt-6 flex items-center justify-between text-xs text-widget-accent/70">
        <div className="flex items-center gap-3">
          <LegendDot className="bg-widget-secondary" label="Available" />
        <LegendDot className="bg-widget-primary/20" label="Unavailable" />
        </div>
        {selectedDate && (
        <p className="rounded-full bg-widget-primary/15 px-4 py-1 font-medium text-widget-accent">
            {format(selectedDate, 'EEEE, MMMM do, yyyy')}
          </p>
        )}
      </div>
    </div>
  )
}

const startOfDay = (date: Date) => {
  const copy = new Date(date)
  copy.setHours(0, 0, 0, 0)
  return copy
}

interface LegendDotProps {
  className?: string
  label: string
}

const LegendDot = ({ className, label }: LegendDotProps) => (
  <span className="flex items-center gap-2">
    <span className={clsx('h-3 w-3 rounded-full', className)} aria-hidden="true" />
    {label}
  </span>
)

export default DatePicker

